'use client';

import React, { useCallback, useContext } from 'react';
import { Box } from '@radix-ui/themes';
import SiteSelector from './SiteSelector';
import useSWRMutation from 'swr/mutation';
import { beehiveFetcher } from '@/services/beehive';
import { ArrowRight } from 'lucide-react';
import { loginWithRedirect } from '@/auth/hooks';
import { ClientAuthContext } from '@/auth/ClientAuthProvider';
import { useUnsavedChanges } from '@/context/unsavedChanges';
import { explorerAnalytics } from '@/lib/analytics';
import { useExplorer } from '@/hooks/useExplorer';

export interface ApiKeyBoxProps {
  options?: IApiKeyBoxOptions;
  onSave: (api_key: string, siteId?: string, siteName?: string) => void;
  value?: string;
  isLoading?: boolean;
}

export interface IApiKeyBoxOptions {
  disable?: boolean;
  label: string;
}

const ShimmerSkeleton = () => (
  <div className="absolute inset-0 z-10 overflow-hidden rounded">
    <div className="h-full w-full bg-gradient-to-r from-shade-neutral-200 via-shade-neutral-100 to-shade-neutral-200 dark:from-shade-neutral-800 dark:via-shade-neutral-700 dark:to-shade-neutral-800 bg-[length:200%_100%] animate-shimmer" />
  </div>
);

export const Login: React.FC<ApiKeyBoxProps> = ({
  value,
  onSave,
  options,
  isLoading = false,
}) => {
  const { isLoggedIn, onLogout, loading } = useContext(ClientAuthContext);
  const { confirmAction } = useUnsavedChanges();
  const { siteName } = useExplorer();
  const { trigger: getApiKey } = useSWRMutation(
    'public/api/resource/dynamic-data',
    beehiveFetcher
  );

  const handleLoginClick = useCallback(() => {
    explorerAnalytics.loginClicked({ source: 'header' });
    confirmAction(() => loginWithRedirect());
  }, [confirmAction]);

  const handleLogoutClick = useCallback(() => {
    explorerAnalytics.logoutClicked({ siteName: siteName || '' });
    confirmAction(() => onLogout?.());
  }, [confirmAction, onLogout, siteName]);

  const handleApiKeyChange = useCallback(
    async (siteId: string, siteName: string) => {
      try {
        const data = await getApiKey(
          new Map()
            .set('site_id', siteName)
            .set('pcv', 'v2')
            .set('api_version', 'v2')
            .set('resource_id', 'customers')
            .set('resource_key', 'customer')
        );
        onSave(data?.apiKey ?? '', siteId, siteName);
      } finally {
      }
    },
    [getApiKey, onSave]
  );

  return (
    <>
      <Box key={value} className="relative">
        {isLoading && <ShimmerSkeleton />}
        <div className={isLoading ? 'opacity-30 pointer-events-none' : ''}>
          {isLoggedIn && !options?.disable ? (
            <div className="flex gap-4 items-center">
              <div className="flex-1 min-w-0">
                <SiteSelector
                  handleApiKeyChange={handleApiKeyChange}
                  isLoading={isLoading}
                />
              </div>
              <div className="flex-shrink-0">
                <button
                  className="flex gap-1 py-1 px-2 justify-center items-center text-[#788E95] font-inter text-sm font-semibold disabled:opacity-50 whitespace-nowrap"
                  onClick={handleLogoutClick}
                  disabled={loading}
                >
                  <Box className="pb-1 flex items-center gap-1 underline cursor-pointer">
                    Log out
                    <ArrowRight className="size-4" />
                  </Box>
                </button>
              </div>
            </div>
          ) : (
            <button
              className="flex gap-1 py-[0.375rem] px-[1.25rem] justify-center items-center bg-shade-teal-500 text-white text-sm rounded-md font-semibold disabled:opacity-50"
              type="button"
              onClick={handleLoginClick}
              disabled={loading}
            >
              Log in
              <ArrowRight className="size-4" />
            </button>
          )}
        </div>
      </Box>
    </>
  );
};

export default Login;
